import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '0.00';
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0%';
  return `${(numeric * 100).toFixed(1)}%`;
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { start: formatLocalDate(start), end: formatLocalDate(today) };
};

const safeParseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export default function SalesAnalyticsReport() {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);

  useEffect(() => {
    const fetchHeaders = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get('https://serverrion.mylksystems.store/billing/getBillHeaders');
        setHeaders(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);
        setError('Unable to load sales data.');
        setHeaders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHeaders();
  }, []);

  const filteredHeaders = useMemo(() => {
    if (!headers.length) return [];

    const from = fromDate ? new Date(fromDate) : null;
    if (from) from.setHours(0, 0, 0, 0);
    const to = toDate ? new Date(toDate) : null;
    if (to) to.setHours(23, 59, 59, 999);

    return headers
      .map((h) => ({
        ...h,
        invDateObj: safeParseDate(h.inv_date),
        netValue: Number(h.net_total || 0),
        excessFlag: Number(h.excessFoodTag || 0) === 1,
      }))
      .filter((h) => {
        if (!from && !to) return true;
        if (!h.invDateObj) return false;
        const afterFrom = from ? h.invDateObj >= from : true;
        const beforeTo = to ? h.invDateObj <= to : true;
        return afterFrom && beforeTo;
      });
  }, [headers, fromDate, toDate]);

  const totals = useMemo(() => {
    const totalBillsCount = filteredHeaders.length;
    const totalBillsValue = filteredHeaders.reduce((sum, h) => sum + (h.netValue || 0), 0);
    const excessBills = filteredHeaders.filter((h) => h.excessFlag);
    const excessBillsCount = excessBills.length;
    const excessBillsValue = excessBills.reduce((sum, h) => sum + (h.netValue || 0), 0);

    const countRatio = totalBillsCount > 0 ? excessBillsCount / totalBillsCount : 0;
    const valueRatio = totalBillsValue > 0 ? excessBillsValue / totalBillsValue : 0;

    return {
      totalBillsCount,
      totalBillsValue,
      excessBillsCount,
      excessBillsValue,
      countRatio,
      valueRatio,
    };
  }, [filteredHeaders]);

  const dailySeries = useMemo(() => {
    const map = new Map();
    for (const h of filteredHeaders) {
      if (!h.invDateObj) continue;
      const key = formatLocalDate(h.invDateObj);
      map.set(key, (map.get(key) || 0) + (h.netValue || 0));
    }
    const days = Array.from(map.keys()).sort();
    const values = days.map((d) => Number(map.get(d) || 0));
    return { days, values };
  }, [filteredHeaders]);

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
        Sales Analytics
      </Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Bills (Count)
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                  {totals.totalBillsCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Bills (Value)
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                  Rs. {formatCurrency(totals.totalBillsValue)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
                <Typography variant="body2" color="text.secondary">
                  Excess Food Bills (Count)
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                  {totals.excessBillsCount} / {totals.totalBillsCount} ({formatPercent(totals.countRatio)})
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
                <Typography variant="body2" color="text.secondary">
                  Excess Food Bills (Value)
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                  Rs. {formatCurrency(totals.excessBillsValue)} / Rs. {formatCurrency(totals.totalBillsValue)} ({formatPercent(totals.valueRatio)})
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              Net Sales (Daily)
            </Typography>
            {dailySeries.days.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No bills found for the selected date range.
              </Typography>
            ) : (
              <LineChart
                height={320}
                xAxis={[{ scaleType: 'point', data: dailySeries.days }]}
                series={[
                  {
                    data: dailySeries.values,
                    label: 'Net Sales (Rs.)',
                    valueFormatter: (v) => `Rs. ${formatCurrency(v)}`,
                    color: '#000000',
                  },
                ]}
                grid={{ horizontal: true, vertical: false }}
              />
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}

